import { stripAttachmentDumps } from "@/lib/utils"

/**
 * Antworttext mit zitiertem Original — für Plain-Text und HTML.
 *
 * Lag als zwei lokale Funktionen in `ReviewCard.tsx`. Damit sah dieselbe
 * Antwort beim Kunden unterschiedlich aus, je nachdem ob sie aus dem Review
 * oder aus dem Posteingang abgeschickt wurde: aus dem Review mit Verlauf, aus
 * dem Posteingang ohne. Ein Kunde soll nicht erkennen können, auf welchen
 * Knopf jemand geklickt hat.
 *
 * Der HTML-Teil ist nicht Kosmetik: ohne ihn frisst die HTML→Text-Wandlung
 * beim Empfänger die Absatzumbrüche und der Verlauf wird zu einer Wand.
 * `{{SIGNATURE}}` ersetzt der Sendepfad serverseitig.
 */

export type ZitatQuelle = {
  sender?: string | null
  sender_email?: string | null
  received_at?: string | null
  original_body?: string | null
  original_html?: string | null
  original_excerpt?: string | null
  /** Vom Portal erzeugt: der „Original-Body" ist unsere interne Erklaerung
   *  („Wir haben am … geantwortet …") — die darf NIE beim Kunden landen. */
  is_proactive?: boolean | null
}

function absender(q: ZitatQuelle): string {
  return q.sender || q.sender_email || "Absender"
}

function datumLang(q: ZitatQuelle): string {
  if (!q.received_at) return ""
  try {
    return new Date(q.received_at).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    })
  } catch {
    return ""
  }
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

/** Plain-Text-Fassung — Fallback für Empfänger ohne HTML-Renderer. */
export function buildBodyWithQuote(
  entwurf: string, quelle: ZitatQuelle, mitVerlauf = true,
): string {
  if (!mitVerlauf || quelle.is_proactive) return entwurf
  const original = stripAttachmentDumps(
    quelle.original_body || quelle.original_excerpt || "",
  ).trim()
  if (!original) return entwurf
  const datum = datumLang(quelle)
  const kopf = datum
    ? `Am ${datum} schrieb ${absender(quelle)}:`
    : `Von: ${absender(quelle)}`
  const zitiert = original.split("\n").map((l) => `> ${l}`).join("\n")
  return `${entwurf.trimEnd()}\n\n${"-".repeat(40)}\n${kopf}\n${zitiert}\n`
}

/** HTML-Fassung mit Blockquote. `{{SIGNATURE}}` setzt der Server ein. */
export function buildHtmlWithQuote(
  entwurf: string, quelle: ZitatQuelle, mitVerlauf = true,
): string {
  const escUser = esc(entwurf).replace(/\n/g, "<br>")
  const datum = quelle.received_at
    ? (() => { try { return new Date(quelle.received_at!).toLocaleString("de-DE") } catch { return "" } })()
    : ""
  const origHtml = (quelle.original_html || "").trim()
  const origText = stripAttachmentDumps(
    quelle.original_body || quelle.original_excerpt || "",
  ).trim()

  const zitatInhalt = !mitVerlauf || quelle.is_proactive
    ? ""
    : (origHtml
      || (origText
        ? `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${esc(origText)}</pre>`
        : ""))

  if (!zitatInhalt) {
    // Kein Verlauf → minimaler HTML-Body mit Text + Signatur
    return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:14px;color:#0f172a">
  <div>${escUser}</div>
  {{SIGNATURE}}
</div>`
  }
  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:14px;color:#0f172a">
  <div>${escUser}</div>
  {{SIGNATURE}}
  <div style="border-top:1px solid #ddd;padding-top:10px;margin-top:10px;font-size:13px;color:#64748b">
    ${datum ? `Am ${datum}` : ""}${absender(quelle) ? ` schrieb ${absender(quelle)}` : ""}:
  </div>
  <blockquote style="margin:10px 0 0 0;padding:10px 15px;border-left:3px solid #cbd5e1;color:#334155">
    ${zitatInhalt}
  </blockquote>
</div>`
}
